import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";
import { Checkbox, FormGroup, Input, Select, Textarea } from "@cofano/cck";
import { IconButton, Tooltip } from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FormField from "components/cck/FormField";
import { FormDialog } from "components/dialogs/Dialogs";
import { Fields, hasOptionalFieldPermission, hasPluginPermission } from "imports/Utils";
import { gql } from "__generated__/gql";
import { CharterInput, Contract, Relation, SalesCharter } from "types/index";
import { formatCharterForMutation } from "../utils";
import SalesCharterShipNominationPeriodRegimeSelect
	from "view/charters/components/selects/SalesCharterShipNominationPeriodRegimeSelect";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import LinkableContractSelect from "view/contractsNew/components/selects/LinkableContractSelect";
import DatePicker from "components/cck/DatePicker";
import { CharterSubtype, CharterType, ContactRoleType, ContractStatus, RelationType } from "__generated__/enums";
import ContactSelect from "view/relations/components/selects/ContactSelect";
import {
	GetDefaultOrderEntryContactsForSalesCharterDocument,
} from "__generated__/view/charters/graphql/charter.queries.generated";
import { Contact } from "types/commons";
import TermsAndConditionsSelect from "view/terms/components/selects/TermsAndConditionsSelect";
import moment from "moment/moment";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import { GetContractsShortDocument } from "__generated__/view/contractsNew/graphql/contract.queries.generated";

type CreateSalesCharterDialogProps = {
	open: boolean;
	onClose: () => void;
	charter?: SalesCharter;
	refetch: (charterId: number) => void;
};

const SalesCharterCreateEditDialog = ({ open, onClose, charter, refetch }: CreateSalesCharterDialogProps) => {

	const session = useStoreState(store => store.session);
	const filterContractsOnActiveStatuses = hasPluginPermission(session, "filterContractsOnActiveStatuses");
	const [ getDefaultOrderEntryContacts ] = useLazyQuery(GetDefaultOrderEntryContactsForSalesCharterDocument);

	const showOrderEntryContactField = hasOptionalFieldPermission(session, Fields.SALES_CHARTER, "orderEntryContact");
	const showDossierNumberField = hasOptionalFieldPermission(session, Fields.SALES_CHARTER, "dossierNumber");
	const showTermsAndConditionsField = hasOptionalFieldPermission(session, Fields.CONTRACT, "termsAndConditions");

	const getDefaultCharterSubtype = () => {
		if (charter == null) {
			const defaultCharterType = session.settings?.settings.defaultSalesCharterType;
			if (Object.values(CharterSubtype).includes(defaultCharterType as CharterSubtype)) {
				return defaultCharterType as CharterSubtype;
			}
			return CharterSubtype.Spot;
		}
	};

	const [ createContract, setCreateContract ] = useState(getDefaultCharterSubtype() === CharterSubtype.Spot);

	const defaultValues = {
		type: CharterType.Sales,
		subtype: getDefaultCharterSubtype(),
		creditor: undefined,
		debtor: undefined,
		broker: undefined,
		relationType: undefined,
		minimumShipNominationDays: undefined,
		shipNominationPeriodRegime: undefined,
		shortNotice: false,
		customerNotes: undefined,
		internalNotes: undefined,
		customerReference: undefined,
		dossierNumber: undefined,
		blDate: undefined,
		contract: undefined,
	};

	const { control, handleSubmit, formState, reset, watch, setValue } = useForm<CharterInput>({
		defaultValues: defaultValues,
	});

	const defaultCreditor = session.settings?.settings.defaultCreditor;
	const defaultDebtor = session.settings?.settings.defaultDebtor;

	const editMode = !!charter;

	const values = watch();

	const linkableContracts = [ CharterSubtype.Contract, CharterSubtype.Time ];
	if (hasPluginPermission(session, "newSpotCharterMayLinkWithExistingContract")) {
		linkableContracts.push(CharterSubtype.Spot);
	}

	const canLinkContract = (subtype: CharterSubtype | null | undefined, debtor: typeof values["debtor"] | null = values.debtor) =>
		!editMode && debtor && subtype && linkableContracts.includes(subtype);

	const canCreateContract = !editMode && values.subtype === CharterSubtype.Spot;

	const [ saveCharter, { loading } ] = useMutation(mutation);

	const [ getRelation ] = useLazyQuery(query);
	const [ getAllLinkableContracts ] = useLazyQuery(GetContractsShortDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const onSave = (data: CharterInput) => {
		const orderEntryContacts = editMode ? undefined : (data.contacts ?? []).map(c => ({
			contactRoleType: ContactRoleType.OrderEntry,
			contact: c,
		}));

		const input = formatCharterForMutation({
			id: charter?.id || 0,
			charterNumber: charter?.charterNumber || "",
			type: CharterType.Sales,
			subtype: data.subtype ?? CharterSubtype.Spot,
			creditorId: data.creditor?.id || 0,
			debtorId: data.debtor?.id || 0,
			brokerId: data.broker?.id,
			relationType: data.relationType,
			contacts: orderEntryContacts,
			minimumShipNominationDays: data.minimumShipNominationDays || null,
			shipNominationPeriodRegime: data.shipNominationPeriodRegime || null,
			shortNotice: data.shortNotice || false,
			customerNotes: data.customerNotes || "",
			internalNotes: data.internalNotes || "",
			customerReference: data.customerReference || "",
			dossierNumber: data.dossierNumber || "",
			blDate: data.blDate,
			contract: data.contract,
		});

		saveCharter({
			variables: {
				input: input,
				createContract: canCreateContract && createContract,
			},
		}).then((response) => {
			onClose();
			refetch(response.data?.saveCharter.id as number);
		});
	};

	const getCreditorRelationTypes = () => {
		return [ RelationType.ApplicationRelation ];
	};

	const getDebtorRelationTypes = () => {
		return [ RelationType.Customer ];
	};

	const setDefaultCreditor = () => {
		if (Number(defaultCreditor) && (Number(defaultCreditor) > 0)) {
			getRelation({
				variables: {
					id: Number(defaultCreditor),
				},
			}).then((response) => {

				const relation = response.data?.relation;

				if (relation?.applicationRelation) {
					setValue("creditor", { id: relation.id, name: relation.name });
				}
			});
		}
	};

	const setDefaultDebtor = () => {
		if (Number(defaultDebtor) && (Number(defaultDebtor) > 0)) {
			getRelation({
				variables: {
					id: Number(defaultDebtor),
				},
			}).then((response) => {

				const relation = response.data?.relation;

				if (relation?.isOperator || relation?.isCustomer) {
					setValue("debtor", { id: relation.id, name: relation.name });
				}
			});
		}
	};

	const setDefaultContract = (subtype: CharterSubtype, debtorId: number | null | undefined = values.debtor?.id, creditorId: number | null | undefined = values.creditor?.id) => {
		getAllLinkableContracts({
			variables: {
				search: {
					charterType: CharterType.Sales,
					charterSubtypes: [ subtype ],
					creditors: creditorId ? [ creditorId ] : [],
					debtors: debtorId ? [ debtorId ] : [],
					isParentOrNoInheritance: true,
					status: filterContractsOnActiveStatuses ? [ ContractStatus.Accepted, ContractStatus.Active, ContractStatus.Finished ] : undefined,
				},
				pagination: {
					size: 999,
					page: 0,
				},
			},
		})
			.then(({ data }) => {
				const contracts = (data?.contracts.content ?? []) as Contract[];

				const parentContracts = contracts.filter(c => c.isParentContract);

				if (parentContracts.length === 1) {
					setValue("contract", parentContracts[0]);
					setValue("broker", parentContracts[0].broker);
				}
			});
	};

	const setDefaultOrderEntryContacts = () => {
		if (values.debtor == null && values.broker == null) {
			setValue("contacts", []);
			return;
		}

		getDefaultOrderEntryContacts({
			variables: {
				search: {
					relations: values.broker ? [ values.broker.id ] : values.debtor ? [ values.debtor.id ] : [],
					roleType: ContactRoleType.OrderEntry,
					isDefault: true,
				},
			},
		}).then(({ data }) => {
			const contacts = (data?.contacts?.content ?? []) as Contact[];

			setValue("contacts", contacts);
		});

	};

	useEffect(() => {
		if (open && charter) {
			reset({
				subtype: charter.subtype,
				creditor: charter.creditor as Relation,
				debtor: charter.debtor as Relation,
				broker: charter.broker ? (charter.broker as Relation) : undefined,
				relationType: charter.relationType || undefined,
				minimumShipNominationDays: charter.minimumShipNominationDays,
				shipNominationPeriodRegime: charter.shipNominationPeriodRegime,
				shortNotice: charter.shortNotice,
				customerNotes: charter.customerNotes,
				internalNotes: charter.internalNotes,
				customerReference: charter.customerReference,
				dossierNumber: charter.dossierNumber,
				blDate: charter.blDateOverride,
				contract: charter.contract ? {
					...charter.contract,
				} : undefined,
			});
		}

	}, [ open ]);

	useEffect(() => {
		if (open && !editMode) {
			setDefaultCreditor();
			setDefaultDebtor();
		}
	}, [ open ]);

	useEffect(() => {
		setDefaultOrderEntryContacts();
	}, [ values.broker, values.debtor ]);

	useEffect(() => {
		if (values.contract && values.contract.id != null) {
			setValue("contract.termsAndConditions", values.contract.termsAndConditions);
		}
	}, [ values.contract?.id ]);

	const getRelationTypes = () => {

		const relationTypes = [];

		const customRelation = values.debtor;

		if (customRelation?.customer) {
			relationTypes.push(RelationType.Customer);
		}

		if (customRelation?.operator) {
			relationTypes.push(RelationType.Operator);
		}

		return relationTypes;
	};

	const showTermsAndConditions = showTermsAndConditionsField && (createContract || (values.subtype === CharterSubtype.Contract || values.subtype === CharterSubtype.Time) && values.contract?.id);

	const isContractRequired = values.subtype === CharterSubtype.Time || values.subtype === CharterSubtype.Contract;

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: charter?.id ? "EDIT_CHARTER" : "CREATE_CHARTER" })}
			form='charterForm'
			loading={loading}>
			<React.Fragment>
				<form onSubmit={(event) => void handleSubmit(onSave)(event)} id="charterForm">
					<FormGroup label={intl.formatMessage({ id: "TYPE" })} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="subtype"
							control={control}
							render={({ field }) => (
								<CharterSubtypeSelect
									{ ...field}
									onChange={(option) => {
										setValue("broker", undefined);
										setValue("relationType", undefined);
										setValue("contract", undefined);
										setCreateContract(option === CharterSubtype.Spot);
										field.onChange(option);

										if (option != null && canLinkContract(option)) {
											setDefaultContract(option);
										}
									}}
									disabled={!!charter?.contract || !!charter?.voyages?.length}
									disableClear
								/>
							)}
						/>
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "CONTRACTOR" })}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name="creditor">
							<Controller
								name="creditor"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<RelationSelect
										{ ...field}
										onChange={(option) => {
											field.onChange(option);
											setValue("relationType", undefined);
											setValue("contract", undefined);

											if (values.subtype != null && canLinkContract(values.subtype)) {
												setDefaultContract(values.subtype, undefined, option?.id);
											}
										}}
										types={getCreditorRelationTypes()}
									/>
								)} />
						</FormField>
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "CUSTOMER" })}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name="debtor">
							<Controller
								name="debtor"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<div style={{ display: "flex", gap: 12 }}>
										<div style={{ flexGrow: 1 }}>
											<RelationSelect
												{ ...field}
												onChange={(option) => {
													field.onChange(option);
													setValue("relationType", undefined);
													setValue("contract", undefined);

													if (values.subtype != null && canLinkContract(values.subtype, option)) {
														setDefaultContract(values.subtype, option?.id, values.creditor?.id);
													}
												}}
												types={getDebtorRelationTypes()} />
										</div>
										<Tooltip title={intl.formatMessage({ id: "CREATE_NEW" })}>
											<IconButton
												size="small"
												onClick={() => window.open("/view/relations/create", "_blank")?.focus()}
											>
												<AddBoxIcon fontSize="small" color="action" />
											</IconButton>
										</Tooltip>
									</div>
								)} />
						</FormField>
					</FormGroup>

					{hasPluginPermission(session, "brokers") && (
						<FormGroup label={intl.formatMessage({ id: "BROKER" })} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="broker"
								control={control}
								render={({ field }) =>
									<RelationSelect
										{ ...field}
										disabled={!!values.contract?.broker}
										types={[ RelationType.Broker ]}
									/>
								} />
						</FormGroup>
					)}

					{charter?.id == null && !editMode && (
						<>
							{showOrderEntryContactField && (
								<FormGroup label={intl.formatMessage({ id: "ORDER_ENTRY" })} labelPlacement="left" overflow="hidden">
									<Controller
										name="contacts"
										control={control}
										render={({ field }) => (
											<ContactSelect
												{...field}
												onChange={option => field.onChange(option ?? undefined)}
												roleType={ContactRoleType.OrderEntry}
												relations={values.broker ? [ values.broker ] : values.debtor ? [ values.debtor ] : []} />
										)} />
								</FormGroup>
							)}

							<FormGroup label={intl.formatMessage({ id: "CONTRACT" })}
								labelPlacement="left"
								overflow="hidden"
								required={isContractRequired}>
								<FormField
									name="contract"
									errors={formState.errors}>
									<Controller
										name="contract"
										control={control}
										rules={{ required: isContractRequired }}
										render={({ field }) => <>
											{canLinkContract(values.subtype) && (
												<div style={{ display: "flex", gap: 12 }}>
													<div style={{ flexGrow: 1 }}>
														<LinkableContractSelect
															{...field}
															disabled={createContract}
															onChange={(contract) => {
																if (!!values.contract?.broker && !contract) {
																	setValue("broker", undefined);
																}
																if (contract?.broker) {
																	setValue("broker", contract.broker);
																}
																field.onChange(contract);
															}}
															additionalSearchOptions={{
																charterType: values.type,
																charterSubtypes: values.subtype ? [ values.subtype ] : undefined,
																creditorId: values.creditor?.id,
																debtorId: values.debtor?.id,
															}}
														/>
													</div>
													{values.subtype !== CharterSubtype.Spot && (
														<Tooltip title={intl.formatMessage({ id: "CREATE_NEW" })}>
															<IconButton
																size="small"
																onClick={() => window.open("/view/contracts/create", "_blank")?.focus()}
															>
																<AddBoxIcon fontSize="small" color="action" />
															</IconButton>
														</Tooltip>
													)}
												</div>
											)}

											{canCreateContract && (
												<div style={{ marginTop: 8 }}>
													<Checkbox
														checked={createContract}
														onChange={(e) => setCreateContract(e.target.checked)}
														label={intl.formatMessage({ id: "CREATE_SPOT_CONTRACT" })}
														disabled={!!values.contract?.id}
													/>
												</div>
											)}
										</>}
									/>
								</FormField>
							</FormGroup>

							{showTermsAndConditions && (
								<FormGroup
									label={intl.formatMessage({ id: "TERMS_AND_CONDITIONS" })}
									labelPlacement="left"
									overflow="hidden"
								>
									<Controller
										name="contract.termsAndConditions"
										control={control}
										render={({ field }) => (
											<TermsAndConditionsSelect
												value={field.value}
												onChange={(option) => field.onChange(option)}
											/>
										)}
									/>
								</FormGroup>
							)}

						</>
					)}

					{getRelationTypes().length > 1 && (
						<FormGroup
							label={`${intl.formatMessage({ id: "ROLE" })} (${intl.formatMessage({ id: "CLIENT" })})`}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<FormField errors={formState.errors} name="relationType">
								<Controller
									name="relationType"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<Select
											fetchOptions={getRelationTypes()}
											getOptionLabel={option => intl.formatMessage({ id: option })}
											disableSearch
											{ ...field} />
									)} />
							</FormField>
						</FormGroup>
					)}

					{editMode && (
						<React.Fragment>
							<FormGroup label={intl.formatMessage({ id: "CARGO_CHARTER_MINIMUM_SHIP_NOMINATION_DAYS" })} labelPlacement="left-center" overflow="visible">
								<Controller
									name="minimumShipNominationDays"
									control={control}
									render={({ field }) => <Input {...field} type="number" addonAfter={intl.formatMessage({ id: "DAYS" })} />} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CARGO_CHARTER_SHIP_NOMINATION_PERIOD_REGIME" })} labelPlacement="left-center" overflow="visible">
								<Controller
									name="shipNominationPeriodRegime"
									control={control}
									render={({ field }) => <SalesCharterShipNominationPeriodRegimeSelect {...field} />} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CARGO_CHARTER_SHORT_NOTICE" })} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="shortNotice"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CUSTOMER_REFERENCE" })} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="customerReference"
									control={control}
									render={({ field }) => <Textarea {...field} />} />
							</FormGroup>

							{showDossierNumberField && (
								<FormGroup label={intl.formatMessage({ id: "DOSSIER_NUMBER" })} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="dossierNumber"
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>
							)}

							<FormGroup
								label={intl.formatMessage({ id: "CARGO_CHARTER_CUSTOMER_NOTES" })}
								labelPlacement="left-center"
								overflow="hidden"
								tooltip={intl.formatMessage({ id: "CARGO_CHARTER_CUSTOMER_NOTES_TOOLTIP" })}
							>
								<Controller
									name="customerNotes"
									control={control}
									render={({ field }) => <Textarea {...field} />} />
							</FormGroup>

							<FormGroup
								label={intl.formatMessage({ id: "CARGO_CHARTER_INTERNAL_NOTES" })}
								labelPlacement="left-center"
								overflow="hidden"
								tooltip={intl.formatMessage({ id: "CARGO_CHARTER_INTERNAL_NOTES_TOOLTIP" })}
							>
								<Controller
									name="internalNotes"
									control={control}
									render={({ field }) => <Textarea {...field} />} />
							</FormGroup>
							<FormGroup label={intl.formatMessage({ id: "BL_DATE" })}
								labelPlacement="left-center"
								overflow="hidden">

								<Controller name="blDate"
									control={control}
									render={({ field }) =>
										<DatePicker {...field} placeholderText={charter?.blDate && !charter.blDateOverride ? moment(charter.blDate).format("DD/MM/YYYY") : ""} />}
								/>
							</FormGroup>

						</React.Fragment>
					)}
				</form>
			</React.Fragment>
		</FormDialog>
	);
};

const mutation = gql(`#graphql
	mutation createCharter_salesCharterCreateDialog($input: CharterInput!, $createContract: Boolean = false) {
		saveCharter(input: $input, createContract: $createContract) {
			id
			type
		}
	}
`);

const query = gql(`#graphql
query relation_createSalesCharterDialog($id: Long!) {
	relation(id: $id) {
		id
		name
		applicationRelation
		isOperator
		isShipManager
		isCustomer
	}
}
`);

export default SalesCharterCreateEditDialog;
